/**
 * A bank-linked account card's connection status (TMMAPP-0120 item 6), in place of the Bank
 * connections section: a `StatusChip` in the registry's `connection` tones — Connected (success),
 * Syncing (info, while the first sync is PENDING / RUNNING, with "{n} imported"), Needs attention
 * (warning: reconnect required, access revoked, or the first sync failed). The chip is a button
 * named "{bank}: {status}" that opens a card-style menu headed with the bank's name: Sync now,
 * Reconnect (only when the connection can be re-authenticated) and Disconnect (the page confirms).
 * A card without a connection shows nothing.
 */
import { useState } from 'react';
import { Box, ButtonBase, MenuItem, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { isReauthable, type ConnectorItem } from '@/api/connectors';
import { StatusChip } from '@/components/StatusChip';
import { statusLabel } from '@/components/statusLabel';
import { ObjectMenu } from '@/shell/OverflowMenu';
import { destructiveItemSx } from '@/shell/objectMenu';
import { resolveTokens } from '@/theme/theme';
import { connectionStateOf } from './connectionState';

export interface ConnectionActions {
  onSync: (item: ConnectorItem) => void;
  onReconnect: (item: ConnectorItem) => void;
  onDisconnect: (item: ConnectorItem) => void;
  /** A sync was queued for this connection a moment ago: Sync now waits (60s, as before). */
  syncBlocked: (item: ConnectorItem) => boolean;
}

interface ConnectionStatusProps {
  item: ConnectorItem;
  /** Absent for a viewer who can't act on the connection: the status shows, with no menu. */
  actions?: ConnectionActions | undefined;
}

export function ConnectionStatus({ item, actions }: ConnectionStatusProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const state = connectionStateOf(item);
  const imported = state === 'SYNCING' ? (item.initialSync?.importedCount ?? null) : null;
  const importedText = imported !== null ? t('connections.imported', { count: imported }) : null;
  const status = statusLabel(t, 'connection', state);
  const bank = item.institutionName.length > 0 ? item.institutionName : t('connections.bank');
  const name = t('connections.statusFor', {
    bank,
    status:
      importedText !== null
        ? t('connections.statusWithImported', { status, imported: importedText })
        : status,
  });
  const close = () => setAnchor(null);

  const body = (
    <>
      <StatusChip domain="connection" status={state} />
      {importedText !== null && (
        <Typography
          variant="caption"
          component="span"
          sx={{ color: tokens.text2, fontVariantNumeric: 'tabular-nums' }}
        >
          {importedText}
        </Typography>
      )}
    </>
  );
  const rowSx = { display: 'inline-flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' };

  if (actions === undefined) {
    return (
      <Box component="span" data-connection-status={state} sx={rowSx}>
        {body}
      </Box>
    );
  }

  return (
    <>
      <ButtonBase
        aria-label={name}
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        data-connection-status={state}
        onClick={(event) => setAnchor(event.currentTarget)}
        sx={{
          ...rowSx,
          borderRadius: '11px',
          '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
        }}
      >
        {body}
      </ButtonBase>
      <ObjectMenu title={bank} anchorEl={anchor} open={anchor !== null} onClose={close}>
        <MenuItem
          disabled={actions.syncBlocked(item)}
          onClick={() => {
            close();
            actions.onSync(item);
          }}
        >
          {t('connections.syncNow')}
        </MenuItem>
        {isReauthable(item.status) && (
          <MenuItem
            onClick={() => {
              close();
              actions.onReconnect(item);
            }}
          >
            {t('connections.reconnect')}
          </MenuItem>
        )}
        <MenuItem
          sx={destructiveItemSx(tokens)}
          onClick={() => {
            close();
            actions.onDisconnect(item);
          }}
        >
          {t('connections.disconnect')}
        </MenuItem>
      </ObjectMenu>
    </>
  );
}
